/**
 * Family context — the active co-parenting space.
 *
 * Loads `auth/me`, lets the user pick an active family (persisted), exposes the
 * caller's `Membership` (for scope gating via @solandia/core/permissions) and
 * the family's children with an active-child selection. Modules read everything
 * they need from here.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Child, Family, MeResponse, Membership } from '@solandia/core';
import { useApi } from '../api/ApiProvider';
import { useAsync } from '../hooks/useAsync';

const ACTIVE_FAMILY_KEY = 'solandia:active-family';

interface FamilyContextValue {
  me: MeResponse;
  family: Family;
  membership: Membership;
  families: Array<{ membership: Membership; family: Family }>;
  setActiveFamily: (familyId: string) => void;
  children: Child[];
  activeChild: Child | null;
  setActiveChild: (childId: string) => void;
  childrenLoading: boolean;
  reloadChildren: () => void;
  /** Refetch `auth/me` (e.g. after the user edits their own profile). */
  reloadMe: () => void;
  /** Refetch the ACTIVE family's authoritative record (carries `mode`). */
  reloadFamily: () => void;
}

const FamilyContext = createContext<FamilyContextValue | null>(null);

export interface FamilyLoaderState {
  status: 'loading' | 'error' | 'no-family' | 'ready';
  error?: Error | null;
  me?: MeResponse;
}

/** Hook that loads `me()`. Exposed so the App can branch on no-family. */
export function useMe() {
  const api = useApi();
  return useAsync<MeResponse>(() => api.me(), []);
}

export function FamilyProvider({
  me,
  reloadMe,
  children: childrenNode,
}: {
  me: MeResponse;
  reloadMe: () => void;
  children: ReactNode;
}) {
  const api = useApi();

  const activeMemberships = useMemo(
    () => me.memberships.filter((m) => m.membership.status === 'active'),
    [me.memberships],
  );

  const [activeFamilyId, setActiveFamilyId] = useState<string>(() => {
    const stored = (() => {
      try {
        return window.localStorage.getItem(ACTIVE_FAMILY_KEY);
      } catch {
        return null;
      }
    })();
    const valid = activeMemberships.find((m) => m.family.id === stored);
    return valid?.family.id ?? activeMemberships[0]?.family.id ?? '';
  });

  const active = activeMemberships.find((m) => m.family.id === activeFamilyId);

  const setActiveFamily = useCallback((familyId: string) => {
    setActiveFamilyId(familyId);
    try {
      window.localStorage.setItem(ACTIVE_FAMILY_KEY, familyId);
    } catch {
      /* ignore */
    }
  }, []);

  const familyId = active?.family.id ?? '';

  const {
    data: childrenList,
    loading: childrenLoading,
    reload: reloadChildren,
  } = useAsync<Child[]>(
    async () => {
      if (!familyId) return [];
      const res = await api.listChildren(familyId);
      return res.items;
    },
    [familyId],
  );

  const childrenItems = useMemo(() => childrenList ?? [], [childrenList]);

  // The `me()` family projection currently omits Mode, so fetch the active
  // family's authoritative record (which carries `mode`/`modeChangedAt`) and
  // prefer it. Reloaded after a mode switch so the gate updates immediately.
  const { data: activeFamilyRecord, reload: reloadFamily } = useAsync<Family | null>(
    async () => (familyId ? await api.getFamily(familyId) : null),
    [familyId],
  );

  const [activeChildId, setActiveChildId] = useState<string>('');
  useEffect(() => {
    if (childrenItems.length === 0) {
      setActiveChildId('');
      return;
    }
    if (!childrenItems.some((c) => c.id === activeChildId)) {
      setActiveChildId(childrenItems[0]!.id);
    }
  }, [childrenItems, activeChildId]);

  const activeChild =
    childrenItems.find((c) => c.id === activeChildId) ?? null;

  // No active membership at all (e.g. only invited): the App handles no-family,
  // but guard here defensively.
  if (!active) {
    return (
      <FamilyContext.Provider value={null as unknown as FamilyContextValue}>
        {childrenNode}
      </FamilyContext.Provider>
    );
  }

  // Normalize the family record: pre-pivot API responses may omit `mode`, so
  // default it to 'family' (the intact-family default) here at the boundary.
  // Everything downstream can read `family.mode` unconditionally.
  const family: Family = {
    ...active.family,
    mode: activeFamilyRecord?.mode ?? active.family.mode ?? 'family',
    modeChangedAt:
      activeFamilyRecord?.modeChangedAt ?? active.family.modeChangedAt ?? null,
  };

  const value: FamilyContextValue = {
    me,
    family,
    membership: active.membership,
    families: activeMemberships,
    setActiveFamily,
    children: childrenItems,
    activeChild,
    setActiveChild: setActiveChildId,
    childrenLoading,
    reloadChildren: () => {
      reloadChildren();
      reloadMe();
    },
    reloadMe,
    reloadFamily,
  };

  return <FamilyContext.Provider value={value}>{childrenNode}</FamilyContext.Provider>;
}

export function useFamily(): FamilyContextValue {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error('useFamily must be used within <FamilyProvider>.');
  return ctx;
}
